import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import { useTheme } from "../../context/ThemeContext";
import { laadAnalytics } from "../../lib/analytics";

/**
 * Cookiebanner voor Google Analytics.
 *
 * De AVG vraagt toestemming vóór analytics-cookies, en die toestemming moet
 * even makkelijk in te trekken zijn als ze te geven is. Daarom:
 *  - de banner verschijnt tot er een keuze gemaakt is (geen keuze = geweigerd,
 *    want de consent-default in public/index.html staat op "denied");
 *  - weigeren staat als een even zichtbare knop naast aanvaarden, niet als een
 *    weggemoffeld linkje;
 *  - via "Cookievoorkeuren" in de footer komt de banner terug, zodat een
 *    gebruiker zijn keuze later kan herzien.
 *
 * De banner staat app-breed, dus ook op de loginpagina: toestemming voor
 * analytics hangt niet af van of je ingelogd bent.
 */

export const CONSENT_KEY = "zichtbaar-dashboard-cookie-consent";
export const OPEN_COOKIE_SETTINGS = "zichtbaar-dashboard:open-cookie-settings";

function leesKeuze() {
  try {
    return window.localStorage.getItem(CONSENT_KEY);
  } catch {
    // Privémodus of geblokkeerde opslag: dan tonen we de banner elke sessie.
    return null;
  }
}

/** Zet de keuze door naar Google Consent Mode én naar de opslag. */
function bewaarKeuze(keuze) {
  try {
    window.localStorage.setItem(CONSENT_KEY, keuze);
  } catch {
    /* Niet kunnen bewaren mag de keuze zelf niet tegenhouden. */
  }
  toepassenKeuze(keuze);
}

/**
 * Vertaalt de keuze naar Google: bij "ja" gaat de consent op granted en halen
 * we gtag.js binnen, bij "nee" blijft alles op de default uit index.html staan
 * en wordt er niets geladen. Intrekken werkt pas vanaf de volgende pagina —
 * een script uit de pagina halen kan niet, dus zetten we de consent terug op
 * denied zodat Google de meting stillegt en de cookies niet meer gebruikt.
 */
function toepassenKeuze(keuze) {
  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      analytics_storage: keuze === "granted" ? "granted" : "denied",
    });
  }
  if (keuze === "granted") laadAnalytics();
}

export default function CookieBanner() {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  // Nog geen keuze gemaakt? Dan vragen we ze meteen bij de eerste render —
  // anders blijft de banner weg.
  const [open, setOpen] = useState(() => !leesKeuze());
  // Aparte vlag zodat de banner van onderaan inschuift in plaats van er te staan.
  const [zichtbaar, setZichtbaar] = useState(false);

  useEffect(() => {
    // Wie eerder al ja zei, wordt niet opnieuw lastiggevallen: dan starten we
    // de meting meteen bij het laden van de pagina.
    if (leesKeuze() === "granted") toepassenKeuze("granted");
    const heropen = () => setOpen(true);
    window.addEventListener(OPEN_COOKIE_SETTINGS, heropen);
    return () => window.removeEventListener(OPEN_COOKIE_SETTINGS, heropen);
  }, []);

  useEffect(() => {
    if (!open) {
      setZichtbaar(false);
      return;
    }
    const id = window.requestAnimationFrame(() => setZichtbaar(true));
    return () => window.cancelAnimationFrame(id);
  }, [open]);

  const kies = useCallback((keuze) => {
    bewaarKeuze(keuze);
    setZichtbaar(false);
    // Even wachten zodat de uitschuifanimatie kan aflopen.
    window.setTimeout(() => setOpen(false), 350);
  }, []);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label="Cookievoorkeuren"
      className="fixed inset-x-0 bottom-0 z-[200] p-4 md:p-6 transition-all duration-300 ease-out"
      style={{
        opacity: zichtbaar ? 1 : 0,
        transform: zichtbaar ? "translateY(0)" : "translateY(1.5rem)",
      }}
    >
      <div
        className={`mx-auto w-full max-w-5xl rounded-xl border p-6 shadow-2xl md:p-8 ${
          isDark
            ? "border-gray-700 bg-gray-900"
            : "border-gray-200 bg-white"
        }`}
      >
        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between md:gap-10">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#0047AB]">
              <Cookie className="h-4 w-4" />
              Cookies
            </div>
            <p
              className={`mt-3 text-sm leading-relaxed ${
                isDark ? "text-gray-300" : "text-gray-600"
              }`}
            >
              We gebruiken Google Analytics om te zien hoe dit platform gebruikt
              wordt, zodat we het kunnen verbeteren. Dat gebeurt enkel met uw
              toestemming. Weigert u, dan werkt alles gewoon verder en meten we
              niets. Meer uitleg staat in ons{" "}
              <Link
                to="/privacy"
                className="font-medium text-[#0047AB] underline underline-offset-2 hover:no-underline"
              >
                privacybeleid
              </Link>
              .
            </p>
          </div>
          {/* Weigeren en aanvaarden even prominent: een "nee" die je moet
              zoeken, is geen vrije keuze. */}
          <div className="flex flex-col gap-3 sm:flex-row md:shrink-0">
            <button
              type="button"
              onClick={() => kies("denied")}
              className={`rounded-md border px-6 py-3 text-sm font-medium transition-colors ${
                isDark
                  ? "border-gray-600 text-gray-200 hover:bg-gray-800"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              Weigeren
            </button>
            <button
              type="button"
              onClick={() => kies("granted")}
              className="rounded-md bg-[#0047AB] px-6 py-3 text-sm font-medium text-white shadow transition-colors hover:bg-[#0047AB]/90"
            >
              Accepteren
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
